import React, { useState, useCallback, useEffect, useRef, useLayoutEffect, useMemo } from 'react';
import { Box, useMediaQuery } from '@mui/material';
import { motion } from 'framer-motion';
import { styled, useTheme } from '@mui/material/styles';
import { Tabs, Tab } from '@mui/material';
import { ChevronLeft, ChevronRight } from 'lucide-react';

// Import tab components
import Stage from './AGTGrowth-products/Stage';
import Metrics from './AGTGrowth-products/Metrics';
import Strategy from './AGTGrowth-products/Strategy';
// (Se ha eliminado la importación de Advisors)

const NavigationButton = styled('button')(({ theme }) => ({
  minHeight: '32px',  // Cambiado de 35px a 32px
  height: '32px',     // Cambiado de 35px a 32px
  width: '32px',      // Cambiado de 35px a 32px
  minWidth: '32px',   // Cambiado de 35px a 32px
  padding: '0',
  marginRight: '8px',  // Ajustado de 8.5px a 8px
  marginLeft: '8px',   // Ajustado de 8.5px a 8px
  border: 'none',
  borderRadius: '30px',
  backgroundColor: 'transparent',
  color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#007BFE',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  zIndex: 1,
  transition: 'all 0.2s',
  outline: 'none',
  '&:hover': {
    backgroundColor: 'transparent',
    opacity: 0.8,
  },
  '&:active': {
    transform: 'scale(0.95)',
    backgroundColor: 'transparent',
  }
}));

const NavigationContainer = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    width: '100%',
    maxWidth: '1872px',
    margin: '0 auto',
    height: '48px',
    display: 'flex',
    justifyContent: 'center',
    '& > div': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '48px',
      background: isWallpaperMode
        ? 'rgba(128, 128, 128, 0.3)'
        : (theme.palette.mode === 'dark' ? '#1C1C1E' : 'rgba(118, 118, 128, 0.20)'),
      backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
      WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
      border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
      borderRadius: '30px',
      overflow: 'hidden'
    }
  };
});

const StyledTabs = styled(Tabs)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    minHeight: '48px',
    height: '48px',
    background: isWallpaperMode
      ? 'rgba(8, 8, 8, 0.3)'
      : (theme.palette.mode === 'dark' ? 'rgba(40, 40, 42, 0.94)' : 'rgba(242, 242, 247, 1)'),
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
    padding: '8px',
    borderRadius: '30px',
    position: 'relative',
    '& .MuiTabs-scroller': {
      overflow: 'visible'
    },
    '& .MuiTabs-indicator': {
      display: 'none'
    },
    '& .MuiTabs-flexContainer': {
      justifyContent: 'center',
      gap: '4px',
      height: '32px'
    }
  };
});

const TabIndicator = styled('div')(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    position: 'absolute',
    height: '32px',
    width: '74px',
    borderRadius: '30px',
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : (theme.palette.mode === 'dark' ? 'rgba(118, 118, 128, 0.24)' : '#FFFFFF'),
    transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    zIndex: 0,
    boxShadow: (isWallpaperMode || theme.palette.mode === 'dark')
      ? 'none'
      : '0 2px 8px rgba(0, 0, 0, 0.04)'
  };
});

const StyledTab = styled(Tab)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    minHeight: '32px',
    height: '32px',
    width: '74px',
    minWidth: '74px',
    padding: '0 8px',
    borderRadius: '30px',
    color: isWallpaperMode
      ? 'rgba(255, 255, 255, 0.7)'
      : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : '#666666'),
    fontSize: '14px',
    fontWeight: 500,
    textTransform: 'none',
    zIndex: 1,
    transition: 'all 0.2s',
    backgroundColor: 'transparent',
    '&.Mui-selected': {
      color: isWallpaperMode ? '#FFFFFF' : '#007BFE'
    },
    '&:hover:not(.Mui-selected)': {
      color: isWallpaperMode ? '#FFFFFF' : '#007BFE',
      backgroundColor: 'transparent'
    }
  };
});

const AGTGrowth = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeTab, setActiveTab] = useState(0);
  const [visibleRange, setVisibleRange] = useState({ start: 0, end: 3 });
  const lastNavigationTime = useRef(Date.now());
  const tabsRef = useRef(null);

  // Solo 3 tabs (eliminado "Advisors") - ahora con useMemo
  const navigationTabs = useMemo(() => [
    { id: 0, label: 'Stage', disabled: false },
    { id: 1, label: 'Metrics', disabled: false },
    { id: 2, label: 'Strategy', disabled: false }
  ], []);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    const mainContainer = document.querySelector('main');
    if (mainContainer) {
      mainContainer.scrollTop = 0;
    }
  }, [activeTab]);

  useEffect(() => {
    const checkMobile = () => {
      const isMobileView = window.innerWidth <= 768;
      if (isMobileView !== isMobile) {
        setVisibleRange({ start: 0, end: 3 });
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [isMobile]);

  useEffect(() => {
    if (!isMobile) {
      setVisibleRange({ start: 0, end: navigationTabs.length });
      return;
    }

    const activeIdx = navigationTabs.findIndex(tab => tab.id === activeTab);
    if (activeIdx === -1) return;

    if (activeIdx < 3) {
      setVisibleRange({ start: 0, end: 3 });
    } else if (activeIdx >= navigationTabs.length - 3) {
      setVisibleRange({ 
        start: navigationTabs.length - 3, 
        end: navigationTabs.length 
      });
    } else {
      setVisibleRange({
        start: activeIdx - 1,
        end: activeIdx + 2
      });
    }
  }, [activeTab, navigationTabs, isMobile]);

  const getIndicatorStyle = useCallback(() => {
    const activeIdx = navigationTabs.findIndex(tab => tab.id === activeTab);
    if (activeIdx === -1) return {};
    
    const visibleIdx = activeIdx - visibleRange.start;
    const offset = visibleIdx * (74 + 4);
    return {
      transform: `translateX(${offset}px)`,
    };
  }, [activeTab, visibleRange.start, navigationTabs]);

  const handleTabChange = useCallback((event, newValue) => {
    const now = Date.now();
    if (now - lastNavigationTime.current >= 300) {
      const tab = navigationTabs.find(tab => tab.id === newValue);
      if (tab?.disabled) {
        return;
      }

      lastNavigationTime.current = now;
      setActiveTab(newValue);
    }
  }, [navigationTabs]);

  const renderContent = useCallback(() => {
    switch (activeTab) {
      case 0:
        return <Stage />;
      case 1:
        return <Metrics />;
      case 2:
        return <Strategy />;
      default:
        return null;
    }
  }, [activeTab]);

  return (
    <Box className="space-y-4">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
      >
        <NavigationContainer>
        {isMobile && activeTab !== 0 && (
          <NavigationButton
            onClick={() => handleTabChange(null, 0)}
            aria-label="Go back"
          >
            <ChevronLeft size={16} strokeWidth={2.5} />
          </NavigationButton>
        )}

        <StyledTabs
          ref={tabsRef}
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons={false}
          aria-label="growth navigation tabs"
        >
          <TabIndicator style={getIndicatorStyle()} />
          {navigationTabs
            .slice(visibleRange.start, visibleRange.end)
            .map((tab) => (
              <StyledTab
                key={tab.id}
                label={tab.label}
                disabled={tab.disabled}
                disableRipple
                value={tab.id}
              />
            ))}
        </StyledTabs>

        {isMobile && visibleRange.end < navigationTabs.length && (
          <NavigationButton
            onClick={() => {
              const nextTab = navigationTabs[visibleRange.end];
              handleTabChange(null, nextTab.id);
            }}
            aria-label="Next section"
          >
            <ChevronRight size={16} strokeWidth={2.5} />
          </NavigationButton>
        )}
      </NavigationContainer>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.08, ease: [0.25, 0.46, 0.45, 0.94] }}
      >
        <Box sx={{ mt: 2, flexGrow: 1 }}>
          {renderContent()}
        </Box>
      </motion.div>
    </Box>
  );
};

// Asegúrate de que el componente se exporte correctamente como default
export default AGTGrowth;